import {
  SESSION_CSRF_HEADER,
  SESSION_CSRF_VALUE,
  sessionResponseSchema,
} from '@coworker/contracts';
import { z } from 'zod';

import type { ApiClient } from '../lib/api.js';
import type { Tokens } from './oauth.js';

/**
 * The three calls that own the session - COW-483, ADR-0039.
 *
 * Until now the SPA redeemed its own authorization code at Cognito's token
 * endpoint and held everything it got back in memory. It now asks our API to do
 * the redemption, and our API keeps the refresh token in a cookie the browser
 * will not show to script. What the SPA still holds is the fifteen-minute
 * access token, in memory, exactly as AUTH-004 decided.
 *
 * Written on top of `ApiClient` rather than beside it. A second HTTP path would
 * miss `REQUEST_TIMEOUT_MS`, the composed `AbortSignal` and the ADR-030 envelope
 * decoding that COW-488 put there - and those are precisely what turns a stalled
 * request into a rendered error instead of a spinner nobody can escape.
 */

/**
 * How long `refresh` and `end` may take. **Not `create` — see below.**
 *
 * Shorter than `REQUEST_TIMEOUT_MS`, which is sized for a route behind a
 * database. Neither of these is: they call Cognito, and the API bounds its own
 * call there at five seconds (`cognito-tokens.ts`), so a route that has not
 * answered in six is not going to.
 *
 * It matters because a person is looking at both. A review measured the
 * default: a first-time visitor whose refresh hung saw "Restoring your
 * session…" for **twelve seconds** on the app's first paint - telling a
 * stranger we were restoring a session they never had.
 *
 * Both can afford to give up. A `refresh` timeout is `unavailable`: no state
 * changes and the retryable error card renders. An `end` timeout still signs
 * out locally and redirects.
 */
const SESSION_TIMEOUT_MS = 6_000;

/**
 * On every session call, and nowhere else.
 *
 * `credentials: 'include'` is what lets the browser store and send the cookie
 * at all; without it the request still succeeds and simply leaves no session
 * behind, which is a silent failure rather than a loud one. The header is the
 * CSRF defence: a cross-site form cannot set it, so requiring it forces a
 * preflight that the gateway refuses for any origin outside its allow-list.
 */
const SESSION_CALL = {
  credentials: 'include',
  headers: { [SESSION_CSRF_HEADER]: SESSION_CSRF_VALUE },
} as const;

/** `refresh` and `end`. `create` deliberately keeps the client default. */
const GIVES_UP_EARLY = { ...SESSION_CALL, timeoutMs: SESSION_TIMEOUT_MS } as const;

export interface CreateSessionInput {
  readonly code: string;
  readonly codeVerifier: string;
  readonly redirectUri: string;
}

export interface SessionApi {
  /** Redeem an authorization code. The cookie comes back on the response. */
  create(input: CreateSessionInput): Promise<Tokens>;
  /** Renew from the cookie alone. Rejects with a 401 `ApiError` when there is none. */
  refresh(): Promise<Tokens>;
  /** Clear the cookie and revoke at Cognito. */
  end(): Promise<void>;
}

export function createSessionApi(api: ApiClient, now: () => number = Date.now): SessionApi {
  /** Cognito answers with a lifetime; everything above here wants a deadline. */
  const tokensFrom = (response: { accessToken: string; idToken: string; expiresIn: number }) => ({
    accessToken: response.accessToken,
    idToken: response.idToken,
    expiresAt: now() + response.expiresIn * 1000,
  });

  return {
    async create(input) {
      // **The client default, not `SESSION_TIMEOUT_MS`, and this is deliberate.**
      // The other two can give up cheaply; this one spends a single-use
      // authorization code. Aborting here does not stop the server - the Lambda
      // finishes the exchange, Cognito marks the code redeemed, and the browser
      // throws away the 201 and its cookie. `completeSignIn` has already taken
      // the verifier and state out of storage, so the tab has nothing to retry
      // with: the person gets "Sign-in did not complete" and a whole new login.
      //
      // The budget is also tightest exactly where the odds are worst. The API
      // may legitimately spend five seconds inside Cognito, and this call
      // arrives on a cold function over a brand-new connection, straight after
      // a cross-origin redirect and however long it took somebody to type a
      // password and clear MFA. Six seconds would leave one for all of that.
      return tokensFrom(
        await api.request('/v1/session', {
          method: 'POST',
          schema: sessionResponseSchema,
          body: input,
          ...SESSION_CALL,
        }),
      );
    },

    async refresh() {
      return tokensFrom(
        await api.request('/v1/session/refresh', {
          method: 'POST',
          schema: sessionResponseSchema,
          ...GIVES_UP_EARLY,
        }),
      );
    },

    async end() {
      await api.request('/v1/session', {
        method: 'DELETE',
        // A 204 carries no body. `z.unknown()` accepts the `undefined` that
        // leaves behind; a stricter schema would turn a correct sign-out into
        // an `UNREADABLE` error.
        schema: z.unknown(),
        ...GIVES_UP_EARLY,
      });
    },
  };
}
